<template>
    <div class="lease-expenses">
        <!-- Breadcrumb / Title -->
        <div class="lease-expenses__breadcrumb">
            <span v-if="!editingExpense && !creatingExpense">Expenses</span>
            <div v-else class="lease-expenses__breadcrumb-nav">
                <a @click="cancelEdit" class="lease-expenses__breadcrumb-link">Expenses</a>
                <span class="lease-expenses__breadcrumb-separator">/</span>
                <span v-if="creatingExpense">Create New Expense</span>
                <span v-if="editingExpense">Edit Expense</span>
            </div>
        </div>

        <!-- Expenses List -->
        <div v-if="!editingExpense && !creatingExpense" class="lease-expenses__list">
            <div v-if="!leases.selectedLease?.expenses?.length" class="lease-expenses__empty">
                <span class="lease-expenses__empty-text">No Expenses Found</span>
                <themed-button
                    v-if="canCreateExpense"
                    variant="default"
                    @click="createExpense"
                >
                    <theme-icon icon="add-icon" type="icons" classes="w-4 h-4" />
                    <span>New Expense</span>
                </themed-button>
                <div v-else class="lease-expenses__no-permission">
                    You don't have permission to create expenses
                </div>
            </div>

            <div v-else>
                <themed-table
                    :columns="columns"
                    :data="leases.selectedLease.expenses"
                    :pagination="false"
                    :searchable="false"
                    :sortable="false"
                    hoverable
                    @action="handleAction"
                />

                <themed-button
                    v-if="canCreateExpense"
                    variant="default"
                    size="sm"
                    @click="createExpense"
                    class="lease-expenses__create-button"
                >
                    <theme-icon icon="add" type="icons" classes="w-4 h-4" />
                    <span>Create Expense</span>
                </themed-button>
            </div>
        </div>

        <!-- Expense Form (Edit/Create) -->
        <div v-else class="lease-expenses__form">
            <!-- Step Navigation -->
            <div class="lease-expenses__steps">
                <button
                    v-for="(step, slug) in visibleSteps"
                    :key="slug"
                    @click="currentStep = slug"
                    :class="['lease-expenses__step', { 'lease-expenses__step--active': slug === currentStep }]"
                    type="button"
                >
                    {{ step.label }}
                    <span v-if="step.showCount" class="lease-expenses__step-count">
                        {{ step.count }}
                    </span>
                </button>
                <button
                    v-if="currentStep === 'escalations'"
                    @click="escalateDown"
                    type="button"
                    class="lease-expenses__escalate-button"
                >
                    Escalate Down
                </button>
                <div v-if="currentStep === 'free_months'" class="lease-expenses__free-months-info">
                    <span class="lease-expenses__free-months-available" v-html="freeMonthsAvailable"></span>
                    Free {{ freeMonthsAvailable | pluralize('Month') }} Available
                    <button
                        @click="$eventHub.$emit('change-section', 'lease-info')"
                        type="button"
                        class="lease-expenses__manage-link"
                    >
                        Manage Free Months
                    </button>
                </div>
            </div>

            <!-- Expense Info Step -->
            <expense-info-step
                v-if="currentStep === 'expense_info'"
                :expense="expense"
                :expense-types="expenseTypes"
                :errors="errors"
                @update:expense="updateExpense"
                @set-expense-type="setExpenseType"
            />

            <!-- Escalations Step -->
            <escalations-step
                v-if="currentStep === 'escalations'"
                :expense="expense"
                :expense-months="expenseMonths"
                @set-escalation="setEscalation"
                @set-previous-input="setPreviousEscalationInput"
            />

            <!-- Free Months Step -->
            <free-months-step
                v-if="currentStep === 'free_months'"
                :expense="expense"
                :expense-months="expenseMonths"
                @set-free-month="setFreeMonth"
            />
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import ThemedButton from '../ThemedButton.vue';
import ThemedTable from '../ThemedTable.vue';
import ExpenseInfoStep from './expense-steps/ExpenseInfoStep.vue';
import EscalationsStep from './expense-steps/EscalationsStep.vue';
import FreeMonthsStep from './expense-steps/FreeMonthsStep.vue';

export default {
    name: 'LeaseExpenses',

    components: {
        ThemedButton,
        ThemedTable,
        ExpenseInfoStep,
        EscalationsStep,
        FreeMonthsStep
    },

    props: {
        formErrors: {
            type: [String, Object],
            default: () => ({})
        }
    },

    data() {
        return {
            editingExpense: false,
            creatingExpense: false,
            currentStep: 'expense_info',
            selectedExpenseId: '',
            expenseTypes: [],
            previousEscalationInput: null,
            freeMonthsAvailable: 0
        };
    },

    computed: {
        ...mapState(['leases']),

        canCreateExpense() {
            return this.$gate.can('create', 'site');
        },

        columns() {
            return [
                {
                    field: 'type',
                    label: 'Type',
                    formatter: (value) => value?.name || '-'
                },
                {
                    field: 'start_date',
                    label: 'Start',
                    align: 'center',
                    formatter: (value) => this.formatDate(value)
                },
                {
                    field: 'end_date',
                    label: 'End',
                    align: 'center',
                    formatter: (value) => this.formatDate(value)
                },
                {
                    field: 'amount',
                    label: 'Base Amount',
                    align: 'right',
                    formatter: (value, row) => this.formatCurrency(value / 100, row.frequency === 'PSF' ? 2 : 0)
                },
                {
                    field: 'frequency',
                    label: 'Frequency'
                },
                {
                    field: 'amount_psf',
                    label: 'PSF',
                    align: 'right',
                    formatter: (value) => this.formatCurrency(value / 100, 2)
                },
                {
                    field: 'is_liability',
                    label: 'Liability?',
                    align: 'center',
                    formatter: (value) => {
                        if (value === null) return '-';
                        return value ? 'Yes' : 'No';
                    }
                },
                {
                    field: 'actions',
                    label: '',
                    type: 'actions',
                    sortable: false,
                    buttons: [
                        {
                            name: 'edit',
                            label: 'Edit',
                            icon: 'edit',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => this.canCreateExpense
                        },
                        {
                            name: 'delete',
                            label: 'Delete',
                            icon: 'trash',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => this.canCreateExpense
                        }
                    ]
                }
            ];
        },

        steps() {
            return {
                expense_info: {
                    label: 'Expense',
                    enabled: true
                },
                escalations: {
                    label: 'Escalations',
                    enabled: this.expense?.id !== 'new',
                    showCount: true,
                    count: (this.expense?.escalations || []).length
                },
                free_months: {
                    label: 'Free Months',
                    enabled: this.expense?.id !== 'new',
                    showCount: true,
                    count: (this.expense?.free_months || []).length
                }
            };
        },

        visibleSteps() {
            const visible = {};
            for (const [slug, step] of Object.entries(this.steps)) {
                if (step.enabled) {
                    visible[slug] = step;
                }
            }
            return visible;
        },

        expenseMonths() {
            if (!this.expense?.start_date) return [];

            const months = [];
            for (let i = 0; i < 12; i++) {
                months.push(moment(this.expense.start_date).add(i, 'months').format('MMM'));
            }
            return months;
        },

        expense() {
            if (!this.selectedExpenseId) return null;
            return _.find(this.leases.selectedLease?.expenses, ['id', this.selectedExpenseId]);
        },

        errors() {
            return this.formErrors || {};
        }
    },

    watch: {
        selectedExpenseId(newId) {
            this.$emit('selectedExpenseChange', newId);
        },

        'leases.selectedLease.expenses': {
            handler() {
                if (this.leases.selectedLease && this.creatingExpense) {
                    const newExpense = _.find(this.leases.selectedLease.expenses, ['id', 'new']);
                    if (!newExpense) {
                        const lastExpense = _.last(this.leases.selectedLease.expenses);
                        if (lastExpense) {
                            this.selectedExpenseId = lastExpense.id;
                            this.creatingExpense = false;
                            this.editingExpense = true;
                        }
                    }
                }
            },
            deep: true
        },

        currentStep(newStep) {
            if (newStep === 'escalations') {
                this.$nextTick(() => this.setEscalations());
            }

            if (newStep === 'free_months') {
                this.$nextTick(() => {
                    this.setFreeMonths();
                    this.calculateFreeMonthsAvailable();
                });
            }
        }
    },

    created() {
        this.$parent.$parent.$on('expenseSaved', (expenseId) => {
            this.creatingExpense = false;
            this.editingExpense = true;
            this.selectedExpenseId = expenseId;
        });
    },

    mounted() {
        this.loadExpenseTypes();

        this.$eventHub.$on('leaseSaved', () => {
            this.calculateFreeMonthsAvailable();
        });
    },

    methods: {
        handleAction({ action, row }) {
            switch (action) {
                case 'edit':
                    this.editExpense(row.id);
                    break;
                case 'delete':
                    this.confirmDeleteExpense(row.id);
                    break;
            }
        },

        async loadExpenseTypes() {
            try {
                const response = await axios.get('/api/expense-types');
                this.expenseTypes = response.data;
            } catch (error) {
                this.$toast?.error('Error loading expense types');
            }
        },

        createExpense() {
            this.editingExpense = false;
            this.creatingExpense = true;

            if (!this.leases.selectedLease.expenses) {
                this.leases.selectedLease.expenses = [];
            }

            this.leases.selectedLease.expenses.push({
                id: 'new',
                expensable_id: this.leases.selectedLease.id,
                expensable_type: 'App\\Lease',
                type_id: '',
                type: { name: '' },
                frequency: '',
                amount_in_dollars: '$0',
                start_date: moment(this.leases.selectedLease.start_date).format('YYYY-MM-DD'),
                end_date: moment(this.leases.selectedLease.end_date).format('YYYY-MM-DD'),
                is_liability: 0,
                is_pass_thru: 0,
                escalations: [],
                free_months: [],
                timeline: {},
                note: ''
            });

            this.selectedExpenseId = 'new';
        },

        editExpense(expenseId) {
            const expense = _.find(this.leases.selectedLease.expenses, ['id', expenseId]);
            if (expense && !expense.escalations) {
                expense.escalations = [];
            }

            this.selectedExpenseId = expenseId;
            this.creatingExpense = false;
            this.editingExpense = true;
        },

        cancelEdit() {
            this.creatingExpense = false;
            this.editingExpense = false;
            this.selectedExpenseId = '';
            this.currentStep = 'expense_info';
        },

        updateExpense(updates) {
            Object.assign(this.expense, updates);
        },

        setExpenseType(typeId) {
            const type = _.find(this.expenseTypes, ['id', parseInt(typeId)]);
            if (type) {
                this.expense.type.name = type.name;
            }
        },

        setEscalation({ year, month, value }) {
            const escalationRecord = _.find(this.expense.escalations, (row) => {
                return row.year_number === year && row.month_number === month;
            });

            if (escalationRecord) {
                escalationRecord.amount = value;
            } else {
                this.expense.escalations.push({
                    expense_id: this.expense.id,
                    year_number: year,
                    month_number: month,
                    amount: value
                });
            }
        },

        setEscalations() {
            if (!this.expense?.escalations) return;

            this.expense.escalations.forEach(escalation => {
                const input = document.querySelector(
                    `#escalations input[data-year="${escalation.year_number}"][data-month="${escalation.month_number}"]`
                );
                if (input) {
                    input.value = escalation.amount;
                }
            });
        },

        setPreviousEscalationInput(input) {
            this.previousEscalationInput = input;
        },

        escalateDown() {
            if (!this.previousEscalationInput) return;

            const year = parseInt(this.previousEscalationInput.dataset.year);
            const month = this.previousEscalationInput.dataset.month;
            const inputs = document.querySelectorAll(`#escalations [data-month="${month}"]`);

            inputs.forEach(input => {
                if (parseInt(input.dataset.year) > year) {
                    input.value = this.previousEscalationInput.value;
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                }
            });
        },

        setFreeMonth({ year, month, checked }) {
            if (checked) {
                this.expense.free_months.push({
                    expense_id: this.expense.id,
                    year_number: year,
                    month_number: month
                });
            } else {
                _.remove(this.expense.free_months, (row) => {
                    return row.year_number === year && row.month_number === month;
                });
            }

            this.calculateFreeMonthsAvailable();
        },

        setFreeMonths() {
            if (!this.expense?.free_months?.length) return;

            this.expense.free_months.forEach(freeMonth => {
                const input = document.querySelector(
                    `#free_months input[data-year="${freeMonth.year_number}"][data-month="${freeMonth.month_number}"]`
                );
                if (input) {
                    input.checked = true;
                }
            });
        },

        calculateFreeMonthsAvailable() {
            this.$nextTick(() => {
                const freeMonths = parseInt(this.leases.selectedLease?.free_months);

                if (isNaN(freeMonths)) {
                    this.freeMonthsAvailable = 0;
                } else {
                    const checkedCount = document.querySelectorAll('#free_months input:checked').length;
                    this.freeMonthsAvailable = freeMonths - checkedCount;
                }

                if (this.freeMonthsAvailable === 0) {
                    this.disableFreeMonths();
                } else {
                    this.enableFreeMonths();
                }
            });
        },

        enableFreeMonths() {
            document.querySelectorAll('#free_months input').forEach(input => {
                input.disabled = false;
            });
        },

        disableFreeMonths() {
            document.querySelectorAll('#free_months input').forEach(input => {
                if (!input.checked) {
                    input.disabled = true;
                }
            });
        },

        confirmDeleteExpense(expenseId) {
            if (expenseId === 'new') {
                this.removeExpense('new');
                return;
            }

            this.$swal({
                title: 'Are you sure?',
                text: 'Are you sure you want to delete this Expense?',
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.deleteExpense(expenseId);
                }
            });
        },

        async deleteExpense(expenseId) {
            try {
                const response = await axios.delete(`/expenses/${expenseId}`);
                this.$toast?.success(response.data.message || 'Expense deleted successfully');
                this.removeExpense(expenseId);
                this.$eventHub.$emit('reloadSite');
            } catch (error) {
                this.$toast?.error(error.response?.data?.message || 'Failed to delete expense');
            }
        },

        removeExpense(expenseId) {
            _.remove(this.leases.selectedLease.expenses, (expense) => expense.id === expenseId);
            this.$emit('filterRequest');
            this.$forceUpdate();
        },

        formatDate(date) {
            return date ? moment(date).format('MM/DD/YYYY') : '-';
        },

        formatCurrency(value, decimals = 0) {
            if (value === null || value === undefined) return '-';
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            }).format(value);
        }
    }
};
</script>
